import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";

import { FormValues } from "../validation";
import { useInvoiceAccountHandler } from "./useInvoiceAccountHandler";

import { AmountTypeEnum } from "api";
import getDateDifference from "shared/functions/dateUtils";
import paymentPurposeService from "services/paymentPurpose";
import { setDuplicateInvoice } from "store/features/ibanInvoiceSlice";
import {
  PAYER_NAME_TOKEN,
  ORDER_NUMBER_TOKEN,
  removeTokens,
} from "shared/functions/ibanQrHelpers";

export const useInvoiceFormState = () => {
  const dispatch = useAppDispatch();

  const invoice = useAppSelector((state) => state.ibanInvoice.duplicateInvoice);

  const allAccounts =
    useAppSelector((state) => state.ibanAccount.accounts) || [];

  const isExpired = useAppSelector(
    (state) => state.subscription.subscription?.isExpired,
  );

  const globalExpiration =
    useAppSelector((state) => state.invoiceExpirationSettings) || {};

  const isLoading = useAppSelector((state) => state.loading.isLoading);

  const { selectedAccount, activeAccounts } = useInvoiceAccountHandler(
    invoice,
    allAccounts,
    isExpired,
    isLoading,
  );

  useEffect(() => {
    return () => {
      if (invoice) {
        dispatch(setDuplicateInvoice(null));
      }
    };
  }, [dispatch, invoice]);

  const invoiceExpiration = useMemo(() => {
    return invoice?.expirationDateTimeUtc
      ? getDateDifference(
          invoice.expirationDateTimeUtc,
          invoice.invoiceDateTimeUtc,
        )
      : null;
  }, [invoice]);

  const expirationDays = invoice
    ? invoiceExpiration?.days
    : globalExpiration?.days;

  const expirationHours = invoice
    ? invoiceExpiration?.hours
    : globalExpiration?.hours;

  const initialValues: FormValues = useMemo(() => {
    const lastPaymentPurpose = paymentPurposeService().getPaymentPurpose();

    const initialAmountType =
      invoice?.amountType || AmountTypeEnum.MerchantDefined;
    const isPayerDefined = initialAmountType === AmountTypeEnum.PayerDefined;

    const resolvedLastPurpose = isPayerDefined
      ? lastPaymentPurpose || ""
      : removeTokens(lastPaymentPurpose || "", [
          PAYER_NAME_TOKEN,
          ORDER_NUMBER_TOKEN,
        ]);

    return {
      ibanAccountUid: selectedAccount?.ibanAccountUid || "new",
      bankName: selectedAccount?.bank?.name || "",
      organizationName: selectedAccount?.organizationName || "",
      identificationCode: selectedAccount?.identificationCode || "",
      iban: selectedAccount?.iban || "",
      publicName: selectedAccount?.publicName || "",

      amountType: initialAmountType,
      amount: invoice?.amount || 0,

      paymentPurpose: invoice?.paymentPurpose || resolvedLastPurpose,

      showNotes: (invoice?.notes || "").length > 0,
      notes: invoice?.notes || "",

      showClientNotes: (invoice?.clientNotes || "").length > 0,
      clientNotes: invoice?.clientNotes || "",

      showInvoiceExpiration: !!expirationDays || !!expirationHours,

      requestPayerFullName:
        invoice?.paymentPurposeOptions?.requestPayerFullName ??
        resolvedLastPurpose.includes(PAYER_NAME_TOKEN),
      requestOrderNumber:
        invoice?.paymentPurposeOptions?.requestOrderNumber ??
        resolvedLastPurpose.includes(ORDER_NUMBER_TOKEN),
      expirationDays,
      expirationHours,
    };
  }, [selectedAccount, invoice, expirationDays, expirationHours]);

  return {
    initialValues,
    activeAccounts,
    isLoading,
  };
};
